import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { AvailabilityGrid, Booking, BookingFilters, BookingSettings } from '../../types/booking.types'
import { todayIsoDate } from '../../utils/bookingGridUtils'
import { fetchAvailabilityGrid, fetchBookingsList, createBookingThunk, cancelBookingThunk, updateBookingThunk, fetchMyBookings, fetchMyBookingSettings, createMyBookingThunk, cancelMyBookingThunk } from '../thunks/bookingThunks'

interface BookingsState {
    grid: AvailabilityGrid | null
    gridLoading: boolean
    gridError: string | null
    bookings: Booking[]
    bookingsLoading: boolean
    bookingsError: string | null
    creating: boolean
    myBookings: Booking[]
    myBookingsLoading: boolean
    myBookingSettings: BookingSettings | null
    myBookingCreating: boolean
}

const initialState: BookingsState = {
    grid: null,
    gridLoading: false,
    gridError: null,
    bookings: [],
    bookingsLoading: false,
    bookingsError: null,
    creating: false,
    myBookings: [],
    myBookingsLoading: false,
    myBookingSettings: null,
    myBookingCreating: false,
}

const bookingsSlice = createSlice({
    name: 'bookings',
    initialState,
    reducers: {
        clearBookingsError: (state) => {
            state.gridError = null
            state.bookingsError = null
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAvailabilityGrid.pending, (state) => {
                state.gridLoading = true
                state.gridError = null
            })
            .addCase(fetchAvailabilityGrid.fulfilled, (state, action: PayloadAction<AvailabilityGrid>) => {
                state.gridLoading = false
                state.grid = action.payload
            })
            .addCase(fetchAvailabilityGrid.rejected, (state, action) => {
                state.gridLoading = false
                state.gridError = String(action.payload ?? action.error.message)
            })
            .addCase(fetchBookingsList.pending, (state) => {
                state.bookingsLoading = true
                state.bookingsError = null
            })
            .addCase(fetchBookingsList.fulfilled, (state, action: PayloadAction<Booking[]>) => {
                state.bookingsLoading = false
                state.bookings = action.payload
            })
            .addCase(fetchBookingsList.rejected, (state, action) => {
                state.bookingsLoading = false
                state.bookingsError = String(action.payload ?? action.error.message)
            })
            .addCase(createBookingThunk.pending, (state) => {
                state.creating = true
            })
            .addCase(createBookingThunk.fulfilled, (state) => {
                state.creating = false
            })
            .addCase(createBookingThunk.rejected, (state) => {
                state.creating = false
            })
            .addCase(cancelBookingThunk.fulfilled, (state, action) => {
                state.bookings = state.bookings.filter(b => b.id !== action.payload)
            })
            .addCase(updateBookingThunk.fulfilled, (state, action) => {
                const idx = state.bookings.findIndex(b => b.id === action.payload.id)
                if (idx >= 0) state.bookings[idx] = action.payload
            })
            .addCase(fetchMyBookings.pending, (state) => {
                state.myBookingsLoading = true
            })
            .addCase(fetchMyBookings.fulfilled, (state, action: PayloadAction<Booking[]>) => {
                state.myBookingsLoading = false
                state.myBookings = action.payload
            })
            .addCase(fetchMyBookings.rejected, (state) => {
                state.myBookingsLoading = false
            })
            .addCase(fetchMyBookingSettings.fulfilled, (state, action: PayloadAction<BookingSettings>) => {
                state.myBookingSettings = action.payload
            })
            .addCase(createMyBookingThunk.pending, (state) => {
                state.myBookingCreating = true
            })
            .addCase(createMyBookingThunk.fulfilled, (state, action: PayloadAction<Booking>) => {
                state.myBookingCreating = false
                state.myBookings = [action.payload, ...state.myBookings]
            })
            .addCase(createMyBookingThunk.rejected, (state) => {
                state.myBookingCreating = false
            })
            .addCase(cancelMyBookingThunk.fulfilled, (state, action) => {
                state.myBookings = state.myBookings.filter(b => b.id !== action.payload)
            })
    },
})

export const { clearBookingsError } = bookingsSlice.actions
export default bookingsSlice.reducer

export const defaultBookingFilters = (): BookingFilters => ({
    tableType: null,
    startDate: todayIsoDate(),
})
